import * as THREE from 'three';
import {
  DEFAULT_MATERIAL_METALNESS,
  DEFAULT_MATERIAL_ROUGHNESS,
  DEFAULT_MATERIAL_ENV_MAP_INTENSITY,
  BLACK_MATERIAL_COLOR,
  BLACK_MATERIAL_METALNESS,
  BLACK_MATERIAL_ROUGHNESS,
  BLACK_MATERIAL_ENV_MAP_INTENSITY,
  BLACK_LIGHTNESS_THRESHOLD,
  ORANGE_MATERIAL_COLOR,
  ORANGE_MATERIAL_METALNESS,
  ORANGE_MATERIAL_ROUGHNESS,
  ORANGE_MATERIAL_ENV_MAP_INTENSITY,
  ORANGE_HUE_MIN,
  ORANGE_HUE_MAX,
  ORANGE_SATURATION_MIN,
} from '../constants/materials';

export interface MaterialProperties {
  metalness: number;
  roughness: number;
  envMapIntensity: number;
  color?: string;
}

/**
 * Detect if a color is black-like based on HSL
 */
export function isBlackLike(hsl: { h: number; s: number; l: number }): boolean {
  return hsl.l <= BLACK_LIGHTNESS_THRESHOLD;
}

/**
 * Detect if a color is orange-like based on HSL
 */
export function isOrangeLike(hsl: { h: number; s: number; l: number }): boolean {
  return (
    hsl.h >= ORANGE_HUE_MIN &&
    hsl.h <= ORANGE_HUE_MAX &&
    hsl.s >= ORANGE_SATURATION_MIN
  );
}

/**
 * Get material properties based on color detection
 */
export function getMaterialProperties(color: THREE.Color): MaterialProperties {
  const hsl = { h: 0, s: 0, l: 0 };
  color.getHSL(hsl);

  if (isBlackLike(hsl)) {
    return {
      metalness: BLACK_MATERIAL_METALNESS,
      roughness: BLACK_MATERIAL_ROUGHNESS,
      envMapIntensity: BLACK_MATERIAL_ENV_MAP_INTENSITY,
      color: BLACK_MATERIAL_COLOR,
    };
  }

  if (isOrangeLike(hsl)) {
    return {
      metalness: ORANGE_MATERIAL_METALNESS,
      roughness: ORANGE_MATERIAL_ROUGHNESS,
      envMapIntensity: ORANGE_MATERIAL_ENV_MAP_INTENSITY,
      color: ORANGE_MATERIAL_COLOR,
    };
  }

  return {
    metalness: DEFAULT_MATERIAL_METALNESS,
    roughness: DEFAULT_MATERIAL_ROUGHNESS,
    envMapIntensity: DEFAULT_MATERIAL_ENV_MAP_INTENSITY,
  };
}

/**
 * Apply material properties to a THREE material
 */
export function applyMaterialProperties(
  material: THREE.Material,
  properties: MaterialProperties
): void {
  if (!(material instanceof THREE.MeshStandardMaterial) || material.type !== 'MeshStandardMaterial') {
    return;
  }

  const meshMaterial = material;

  // Apply properties
  meshMaterial.metalness = properties.metalness;
  meshMaterial.roughness = properties.roughness;
  meshMaterial.envMapIntensity = properties.envMapIntensity;

  // Apply color if specified
  if (properties.color && meshMaterial.color) {
    meshMaterial.color.set(properties.color);
  }

  meshMaterial.needsUpdate = true;
}
